/**
 * ROUND8: a tracker dog, as the quiet-path gate models it (docs/milestones/ROUND8-contracts.md §2). The
 * gate cannot follow a trail the ranger has not laid yet, so it takes the cautious side of what the dog
 * can do the moment it is near him: a nose that finds a crawling man all round it - through a wall, down
 * in a trench, in the dark - out to its radius with the wind at his back (`DOGS.noseRadius` x
 * `DOGS.downwind`), plus the pace it may walk off its handler's shoulder. It goes where the handler goes:
 * his patrol, looked at step by step as his eyes are (model.ts); a dog with no walking handler is a nose
 * that never moves.
 */

import { PI } from '../../../sim/math';
import { DOGS } from '../../../sim/tuningDogs';
import type { MissionDef, SpawnDef } from '../../../sim/types';
import { QUIET } from '../../tuning';
import type { Look } from './model';

/** How far the model's dog smells a crawling man, metres: the downwind nose, from wherever it walks at heel. */
export const NOSE_REACH = DOGS.noseRadius * Math.max(1, DOGS.downwind) + DOGS.heelDistance;

/** A nose where `at` stands: all round, no line of sight, and a trench hides nothing from it. */
export function noseAt(at: { x: number; z: number }): Look {
  return {
    x: at.x, z: at.z, facing: 0, half: PI, range: NOSE_REACH, sense: NOSE_REACH, eye: QUIET.eye, ignoreProp: -1,
    inTrench: true, smell: true,
  };
}

/** The beat the dog walks: its handler's patrol, when it has a handler who walks one; otherwise null. */
export function handlerBeat(
  def: Pick<MissionDef, 'spawns'>, dog: SpawnDef,
): ReadonlyArray<readonly [number, number]> | null {
  const handler = dog.handler === undefined ? undefined : def.spawns[dog.handler];
  if (!handler || handler.kind === 'dog' || handler.dormantUntil !== undefined) return null;
  const beat = handler.patrol;
  return beat !== undefined && beat.length > 1 ? beat : null;
}
